<template>
  <div>
    <modal :is-open="isOpen" @close="closeModal" :footerClear="confirmationStep()"
      :dialog-class="confirmationStep() ? 'modal__dialog--for-tour-confirmation' : ''">
      <template #header v-if="showHeader">
        <h6 class="font-body-normal text-color-sapphire-5 mb-0">
          {{ `Book a tour with ${facilityName}`.toUpperCase() }}
        </h6>
        <steps v-if="!offerFlow" :current-step="currentStep + 1" :total-steps="totalSteps - 1" />
        <h5 class="font-heading-20 mb-0" :class="{ 'mt-2': offerFlow }"> {{ stepTitle }} </h5>
      </template>
      <component :is="currentStepComponent" v-bind="currentProperties"></component>
      <template #footer>
        <button-container>
          <system-button style-type="clear" @click="onPrevStep"
            v-if="(offerFlow || currentStep != 0) && !confirmationStep()">
            {{ prevBtnText }}
          </system-button>

          <system-button @click="onNextStep" :disabled="disableButton"
            :color="localTourData.cancelChecked ? 'rust' : 'sapphire'">
            {{ nextStepBtnText }}
          </system-button>
        </button-container>
      </template>
    </modal>
    <loader :loading="loading" />
  </div>
</template>

<script>
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import Loader from '@components/loader/loader.vue';
import Modal from '@components/modal/modal.vue';
import Steps from '@components/progress/steps.vue';
import SystemButton from '@components/buttons/system-button.vue';
import ButtonContainer from '@components/layout/button-container.vue';
import dependentsOlderThanFacilityAgeRange from '@mixins/dependentsOlderThanFacilityAgeRange';
import FamilyInfo from '@parent/components/schedule-tour/family-info.vue';
import SelectCareSchedules from '@parent/components/schedule-tour/select-care-schedules.vue';
import SelectDaysOfWeek from '@components/legup/select-week-days.vue';
import DateAndTime from '@parent/components/schedule-tour/date-time.vue';
import Confirmation from '@parent/components/schedule-tour/confirmation.vue';
import { regexPatterns } from '@utils';

export default {
  name: 'legup-tour',
  components: {
    Loader,
    Modal,
    Steps,
    SystemButton,
    ButtonContainer,
    FamilyInfo,
    SelectCareSchedules,
    SelectDaysOfWeek,
    DateAndTime,
    Confirmation,
  },
  mixins: [dependentsOlderThanFacilityAgeRange],
  props: {
    isOpen: {
      type: Boolean,
      required: false,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    edit: {
      type: Boolean,
    },
    bookedTour: {
      type: Object,
    },
    facilityName: {
      type: String,
      required: true,
    },
    facilityTimezone: {
      type: String,
      required: true,
    },
    facilityPrograms: {
      type: Array,
    },
    legupCenterId: {
      type: Number,
      required: true,
    },
    offerDetails: {
      type: Object,
    },
    fetchOfferData: {
      type: Function,
    },
    seatId: {
      type: Number,
    },
  },
  data() {
    return {
      currentStep: 0,
      dependent: {},
      ageGroupPrograms: [],
      loading: false,
      localTourData: {
        offerDetails: this.offerDetails || {},
        cancelChecked: false,
        dependentId: undefined,
        seatId: this.seatId,
        startCare: '', // ToDo consider this as preferred_date to determine age_group along with DOB
        phoneNumber: '',
        parentComment: '',
        selectedSchedule: [],
        selectedWeekDays: [],
        selectedTour: {
          start: '',
          duration: '',
          isOpenHouse: false,
          tourScheduleId: undefined,
          virtual: '',
          virtualInstructions: '',
          virtualLink: '',
        },
      },
      bus: new Vue(),
    };
  },
  mounted() {
    if (!this.childAgeGroupId) {
      this.fetchDependentAgeGroupId();
    }
    this.localTourData.phoneNumber = this.$store.state.user.current.phoneNumber;
  },
  computed: {
    offerFlow() {
      return this.offerDetails;
    },
    currentStepComponent() {
      return this.activeSteps()[this.currentStep].component;
    },
    dependentValues() {
      return Object.values(this.$store.state.dependents.list);
    },
    currentProperties() {
      return {
        tourData: this.edit ? this.bookedTour : this.localTourData,
        olderChildren: this.dependentsOlderThanFacilityAgeRange(this.dependentValues),
        facilityName: this.facilityName,
        timezone: this.facilityTimezone,
        centerId: this.legupCenterId,
        ageGroupPrograms: this.ageGroupPrograms,
        onChange: this.onChange,
        bus: this.bus,
        submitStep: this.submitStep,
      };
    },
    totalSteps() {
      return Object.keys(this.activeSteps()).length;
    },
    stepName() {
      return this.activeSteps()[this.currentStep].stepName;
    },
    showHeader() {
      return !['confirmation'].includes(this.stepName);
    },
    nextStepBtnText() {
      return this.activeSteps()[this.currentStep].nextBtnText;
    },
    prevBtnText() {
      return this.activeSteps()[this.currentStep].prevBtnText;
    },
    stepTitle() {
      return this.activeSteps()[this.currentStep].title;
    },
    disableButton() {
      if (this.loading) return true;

      const activeStep = this.stepName;
      let disable = false;
      if (activeStep === 'familyInfo') {
        disable = !regexPatterns.mobileNumber.regex.test(this.localTourData.phoneNumber)
          || !this.localTourData.phoneNumber || !this.localTourData.dependentId;
      } else if (activeStep === 'careSchedule') {
        const hasSchedule = this.ageGroupPrograms.length > 0;
        disable = hasSchedule ? !this.localTourData.selectedSchedule.length : false;
      } else if (activeStep === 'dateTime') {
        disable = this.localTourData.cancelChecked
          ? false : !this.localTourData.selectedTour.start?.length;
      }
      return disable;
    },
    childAgeGroupId() {
      return this.$store.state.legup.dependentAgeGroup;
    },
    chooseWeekDays() {
      if (this.ageGroupPrograms.length) {
        const notFullTime = this.ageGroupPrograms
          .find((s) => s.numberOfDays < 5 && s.daysOfTheWeek.length !== s.numberOfDays);
        return notFullTime !== undefined;
      }
      return false;
    },
  },
  methods: {
    selectedDependent() {
      return this.$store.getters['dependents/dependentById'](this.localTourData.dependentId);
    },
    hasPrograms() {
      if (!this.facilityPrograms) {
        return false;
      }

      return this.facilityPrograms.filter((p) => p.legupAgeGroupId).length > 0;
    },
    activeSteps() {
      if (this.offerFlow) {
        return this.offerTourSteps();
      }
      return this.stepsData().filter((s) => (this.edit ? s.editable : s.display));
    },
    confirmationStep() {
      return this.stepName === 'confirmation';
    },
    async onChange(data) {
      const newLocalTourData = cloneDeep(this.localTourData);
      let resetSchedules = false;
      Object.keys(data).forEach((key) => {
        if ((key === 'dependentId' && newLocalTourData[key] !== data[key])
          || (key === 'startCare' && newLocalTourData[key] !== data[key])
        ) {
          resetSchedules = true;
        }
        newLocalTourData[key] = data[key];
      });
      if (resetSchedules) {
        newLocalTourData.selectedSchedule = [];
        newLocalTourData.selectedWeekDays = [];
        await this.fetchDependentAgeGroupId();
        await this.ageGroupProgramSchedules();
      }
      this.localTourData = { ...newLocalTourData };
    },
    onPrevStep() {
      const activeStep = this.stepName;
      if (activeStep === 'familyInfo') {
        this.closeModal();
      } else if (this.currentStep > 0) {
        this.currentStep -= 1;
      }
    },
    onNextStep() {
      this.bus.$emit('on-next-step');
    },
    async submitStep() {
      const activeStep = this.activeSteps()[this.currentStep];
      if (activeStep.nextBtnText === 'Submit' && !this.localTourData.cancelChecked) {
        this.loading = true;
        if (this.edit) {
          await this.rescheduleTour();
          this.closeModal();
        } else {
          await this.handleBookTour();
          this.currentStep += 1;
        }
        this.loading = false;
      } else if (this.localTourData.cancelChecked) {
        this.loading = true;
        await this.cancelTour();
        this.loading = false;
        this.closeModal();
        this.currentStep = 0;
        this.localTourData.cancelChecked = false;
      } else if (activeStep.stepName === 'familyInfo' && this.hasPrograms()) {
        if (this.dependent.id !== this.localTourData.dependentId) {
          this.ageGroupPrograms = [];
          this.dependent = this.selectedDependent();
          this.loading = true;
          await this.fetchDependentAgeGroupId();
          await this.ageGroupProgramSchedules();
          this.loading = false;
        }
        this.currentStep += 1;
      } else if (activeStep.nextBtnText === 'Okay') {
        this.currentStep = 0;
        this.closeModal();
      } else if (this.currentStep < 3) {
        // increment step
        this.currentStep += 1;
      }
    },

    async fetchDependentAgeGroupId() {
      let { dateOfBirth } = this.dependent;
      if (this.bookedTour && this.bookedTour.child) {
        dateOfBirth = this.bookedTour.child.birthDate;
      }

      if (dateOfBirth) {
        await this.$store.dispatch('legup/dependentAgeGroup', {
          centerId: this.legupCenterId,
          childBirthDate: dateOfBirth,
          preferredDate: this.localTourData.startCare,
        });
      }
    },

    async ageGroupProgramSchedules() {
      if (!this.childAgeGroupId) {
        await this.fetchDependentAgeGroupId();
      }

      const programs = await this.$store.dispatch('legup/ageGroupProgramSchedules', { id: this.childAgeGroupId });

      if (programs.length) {
        this.ageGroupPrograms = programs;
      }
    },
    async handleBookTour() {
      if (this.offerFlow) {
        await this.bookOfferTour();
      } else {
        await this.bookRegularTour();
      }
    },
    async bookRegularTour() {
      const {
        id, name, firstName, lastName, email, legupParentId,
      } = this.$store.getters['user/currentParent'];
      const parent = {
        id,
        legupParentId,
        name,
        firstName,
        lastName,
        email,
        phoneNumber: this.localTourData.phoneNumber,
      };
      const {
        id: dependentId,
        firstName: cFirstName,
        lastName: cLastName,
        ageMonths,
        dateOfBirth,
      } = this.dependent;
      const child = {
        firstName: cFirstName,
        lastName: cLastName,
        ageMonths,
        birthDate: dateOfBirth,
        id: dependentId,
      };
      const enrollmentId = this.$route.query.er_id;
      await this.$store.dispatch('tours/bookTour', {
        centerId: this.legupCenterId,
        parent,
        child,
        tourInfo: this.localTourData,
        noFamilyNotes: false,
        ageGroupId: this.childAgeGroupId,
        enrollmentId,
      });
      const newTour = await this.$store.getters['tours/upcomingTour'];
      await this.$store.dispatch('user/fetchCurrent');
      this.$emit('tour-booked', { tour: newTour, ageGroupId: this.childAgeGroupId });
    },
    async bookOfferTour() {
      const parent = {
        legupParentId: this.offerDetails.parentId,
        firstName: this.offerDetails.parentFirstName,
        lastName: this.offerDetails.parentLastName,
        email: this.offerDetails.parentEmail,
        phoneNumber: this.offerDetails.parentPhoneNumber,
      };
      const child = {
        id: this.offerDetails.dependentId,
        firstName: this.offerDetails.childFirstName,
        lastName: this.offerDetails.childLastName,
        ageMonths: this.offerDetails.childAgeMonths,
        birthDate: this.offerDetails.childDob,
      };
      await this.$store.dispatch('tours/bookTour', {
        centerId: this.offerDetails.centerId,
        parent,
        child,
        tourInfo: this.localTourData,
        noFamilyNotes: true,
        ageGroupId: this.childAgeGroupId,
        enrollmentId: undefined,
        seatId: this.offerDetails.seatId,
      });
      await this.$store.dispatch('legup/fetchSeatOfferDetails', this.offerDetails.seatId);
      this.$emit('tour-booked', { newSeatOfferState: this.$store.state.legup.seatOfferDetails.offerState });
      this.fetchOfferData(true);
    },
    async rescheduleTour() {
      if (this.bookedTour.startTime === this.localTourData.selectedTour.start) {
        return;
      }
      const params = {
        tour: this.bookedTour,
        newDateTime: this.localTourData.selectedTour,
      };
      await this.$store.dispatch('tours/rescheduleTour', { params });
      const newTour = await this.$store.getters['tours/upcomingTour'];
      this.$emit('tour-booked', { tour: newTour, ageGroupId: this.childAgeGroupId });
      if (this.offerFlow) this.fetchOfferData(true);
    },
    async cancelTour() {
      await this.$store.dispatch('tours/cancelTour', { tour: this.bookedTour });
      this.$emit('tour-cancelled');
      if (this.offerFlow) this.fetchOfferData(true);
    },
    createBaseStep(extraProperties = {}) {
      return {
        display: true,
        editable: false,
        prevBtnText: '',
        nextBtnText: 'Okay',
        ...extraProperties,
      };
    },
    offerTourSteps() {
      const dateAndTimeStep = {
        component: DateAndTime,
        title: 'Choose tour date and time',
        stepName: 'dateTime',
        ...this.createBaseStep({
          editable: true,
          prevBtnText: 'Back',
          nextBtnText: 'Submit',
        }),
      };

      const confirmationStep = {
        component: Confirmation,
        stepName: 'confirmation',
        ...this.createBaseStep(),
      };

      return [dateAndTimeStep, confirmationStep];
    },
    stepsData() {
      const familyInfoStep = {
        component: FamilyInfo,
        title: 'Add family info',
        stepName: 'familyInfo',
        ...this.createBaseStep({
          prevBtnText: 'Cancel',
          nextBtnText: 'Next',
        }),
      };

      const careScheduleStep = {
        component: SelectCareSchedules,
        title: 'Select care schedule',
        stepName: 'careSchedule',
        ...this.createBaseStep({
          display: this.hasPrograms(),
          prevBtnText: 'Back',
          nextBtnText: 'Next',
        }),
      };

      const scheduleWeekDays = {
        component: SelectDaysOfWeek,
        title: 'Select care schedule',
        stepName: 'selectWeekDays',
        ...this.createBaseStep({
          display: this.chooseWeekDays,
          prevBtnText: 'Back',
          nextBtnText: 'Next',
        }),
      };

      const dateAndTimeStep = {
        component: DateAndTime,
        title: 'Choose tour date and time',
        stepName: 'dateTime',
        ...this.createBaseStep({
          editable: true,
          prevBtnText: 'Back',
          nextBtnText: this.localTourData.cancelChecked ? 'Cancel tour' : 'Submit',
        }),
      };

      const confirmationStep = {
        component: Confirmation,
        stepName: 'confirmation',
        ...this.createBaseStep(),
      };

      return [
        familyInfoStep,
        careScheduleStep,
        scheduleWeekDays,
        dateAndTimeStep,
        confirmationStep,
      ];
    },
  },
};
</script>
